// Voice synthesis with simulated radio jamming.
//
// Strategy:
//  - SpeechSynthesisUtterance for the actual voice (browser-built TTS).
//  - Web Audio for ambient radio static + click chirps before/after each line.
//  - Queue utterances so multiple AI don't overlap (chat radio etiquette).
//
// AudioContext.resume() must be called from a user gesture (R8).

import { type Personality } from '../ai/personalities';

interface QueueItem {
  text: string;
  pitch: number;
  rate: number;
  voiceHint?: string;
  onStart?: () => void;
  onEnd?: () => void;
}

export class VoiceSynth {
  available: boolean;
  ctx: AudioContext | null = null;
  private staticBus: GainNode | null = null;
  private staticBuf: AudioBuffer | null = null;
  private queue: QueueItem[] = [];
  private active = false;
  private chineseVoice: SpeechSynthesisVoice | null = null;
  private resumed = false;

  constructor() {
    this.available = typeof speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined';
    if (this.available) {
      const tryPick = (): void => {
        const all = speechSynthesis.getVoices();
        const zh = all.find((v) => /zh|cmn|Mandarin|中文/i.test(v.lang) || /chinese/i.test(v.name));
        if (zh) this.chineseVoice = zh;
      };
      tryPick();
      speechSynthesis.onvoiceschanged = tryPick;
    }
  }

  /** Must be called from a user gesture (R8). */
  resume(): void {
    if (!this.ctx) {
      try { this.ctx = new (window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext!)(); } catch { return; }
      this.staticBuf = this.makeNoiseBuffer(this.ctx, 1.4);
      this.staticBus = this.ctx.createGain();
      this.staticBus.gain.value = 0.0;
      this.staticBus.connect(this.ctx.destination);
      this.startStaticLoop();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
    this.resumed = true;
  }

  private makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    return buf;
  }

  private startStaticLoop(): void {
    if (!this.ctx || !this.staticBuf || !this.staticBus) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.staticBuf;
    src.loop = true;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.value = 1800;
    filt.Q.value = 1.6;
    src.connect(filt).connect(this.staticBus);
    src.start();
  }

  private playClick(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = 1700;
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(0.18, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.085);
    osc.connect(gain).connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  private setStaticVolume(v: number): void {
    if (!this.ctx || !this.staticBus) return;
    this.staticBus.gain.cancelScheduledValues(this.ctx.currentTime);
    this.staticBus.gain.linearRampToValueAtTime(v, this.ctx.currentTime + 0.08);
  }

  /** Queue a line for sequential speaking. */
  speak(text: string, p: Personality, onStart?: () => void, onEnd?: () => void): void {
    if (!this.available || !text) {
      onStart?.();
      onEnd?.();
      return;
    }
    this.queue.push({ text, pitch: p.voicePitch, rate: p.voiceRate, onStart, onEnd });
    this.drain();
  }

  private drain(): void {
    if (this.active) return;
    const next = this.queue.shift();
    if (!next) return;
    this.active = true;

    if (this.resumed) {
      this.playClick();
      this.setStaticVolume(0.04);
    }

    const u = new SpeechSynthesisUtterance(next.text);
    u.lang = 'zh-CN';
    if (this.chineseVoice) u.voice = this.chineseVoice;
    u.pitch = clamp(next.pitch, 0.5, 2);
    u.rate = clamp(next.rate, 0.6, 1.6);
    u.volume = 1.0;
    u.onstart = () => next.onStart?.();
    const finish = () => {
      next.onEnd?.();
      this.setStaticVolume(0);
      if (this.resumed) this.playClick();
      this.active = false;
      // small gap before next line
      setTimeout(() => this.drain(), 200);
    };
    u.onend = finish;
    u.onerror = finish;

    try {
      speechSynthesis.speak(u);
    } catch {
      finish();
    }
  }

  cancelAll(): void {
    this.queue.length = 0;
    if (this.available) try { speechSynthesis.cancel(); } catch { /* ignore */ }
    this.active = false;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
